import React, { useState, useRef, useEffect, forwardRef } from 'react';
import { sanitize } from 'config/xssConfig';
import { Clock, Send } from 'lucide-react';
import TextEditor from 'components/TextEditor';
import EntityPrivacy from 'components/Entities/EntityPrivacy';
import { PrivacyLevel } from 'util/userUtils';
import { useUser } from 'util/UserContext';
import { transformHtmlContent } from 'util/transformHtmlContent';
import { formatDate, formatRelativeTime } from 'config/dayjsConfig';
import EntityAvatar from 'components/Entities/EntityAvatar';
import CoauthorSelect, { CoauthorSuggestion } from 'components/Coauthors/CoauthorSelect';
import { loadCoauthorData, handleCoauthorSelect as selectCoauthor, handleCoauthorRemove as removeCoauthor } from 'util/entityUtils';
import DetailPanel from 'ui/DetailPanel';
/**
 * Props for the CommentInput component
 */
interface CommentInputProps {
  onSubmit: (content: string, privacy: PrivacyLevel, coauthors: CoauthorSuggestion[], addedCoauthors: CoauthorSuggestion[], removedCoauthors: CoauthorSuggestion[]) => void;
  onCancel?: () => void;
  buttonText: string;
  initialContent?: string;
  initialPrivacy?: PrivacyLevel;
  action?: 'add' | 'reply' | 'edit' | 'form';
  commentData?: {
    id: number;
    user_id?: number | undefined;
    username: string;
    content?: string;
    updated_at?: string;
    created_at?: string;
    properties?: {
      coauthors?: CoauthorSuggestion[];
    };
  };
  onCoauthorsChange?: (addedCoauthors: CoauthorSuggestion[], removedCoauthors: CoauthorSuggestion[]) => void;
}

/**
 * CommentInput component for adding, replying to, or editing comments
 */
const CommentInput = forwardRef<HTMLDivElement, CommentInputProps>(({
  onSubmit,
  onCancel,
  buttonText,
  initialContent = '',
  initialPrivacy = 'public',
  action = 'add',
  commentData,
  onCoauthorsChange,
}, ref) => {
  const [content, setContent] = useState(initialContent);
  const { currentUser, awaitingUser } = useUser();
  const [privacy, setPrivacy] = useState<PrivacyLevel>(initialPrivacy || currentUser?.properties?.privacy_content_default_audience?.value|| 'public');
  const [isExpanded, setIsExpanded] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editorKey, setEditorKey] = useState<number>(0);
  const [coauthors, setCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [existingCoauthors, setExistingCoauthors] = useState<CoauthorSuggestion[]>(commentData?.properties?.coauthors || []);
  const [newCoauthors, setNewCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [removedCoauthors, setRemovedCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [pendingCoauthors, setPendingCoauthors] = useState<CoauthorSuggestion[]>([]);
  const [isLoadingCoauthors, setIsLoadingCoauthors] = useState<boolean>(false);

  const internalRef = useRef<HTMLDivElement>(null);
  // Use the provided ref if available, otherwise use internal ref
  const inputRef = ref || internalRef;

  useEffect(() => {
    if (awaitingUser) {
      return;
    }
    if (currentUser && currentUser?.properties?.privacy_content_default_audience && action !== 'edit') {
      setPrivacy(currentUser?.properties?.privacy_content_default_audience.value);
    } else {
      setPrivacy(initialPrivacy || 'public');
    }
  }, [currentUser, awaitingUser, action, initialPrivacy]);

  // Focus editor when mounting for edit/reply actions
  useEffect(() => {
    if (action === 'edit' || action === 'reply') {
      // Add a slight delay to ensure the editor is properly initialized
      setTimeout(() => {
        if (inputRef && 'current' in inputRef && inputRef.current) {
          // Try to focus the editor content
          const editor = inputRef.current.querySelector('[contenteditable=true]');
          if (editor instanceof HTMLElement) {
            editor.focus();
          }
        }
      }, 100);
    }
  }, [action, editorKey, inputRef]);

  const handleCancel = () => {
    // Reset content
    setContent('');
    if (action !== 'add') {
      setIsExpanded(false);
    }
    setEditorKey(prev => prev + 1);
    
    // Call onCancel if provided
    if (onCancel) {
      onCancel();
    }
  };

  const handleSubmit = () => {
    if (!content.trim() || content.trim() === '<p></p>') {
      setError('Comment cannot be empty');
      return;
    }
    
    // Sanitize content before submitting
    const sanitizedContent = sanitize(content);
    onSubmit(sanitizedContent, privacy, coauthors, newCoauthors, removedCoauthors);
    setContent('');
    setPrivacy(currentUser?.properties?.privacy_content_default_audience?.value || initialPrivacy || 'public');
    // Only collapse for edit/reply actions
    if (action !== 'add') {
      setIsExpanded(false);
    }
    setEditorKey(prev => prev + 1);
    if (onCancel) {
      onCancel();
    }
  };

  // Determine if the component should be in fullwidth bottom position
  const isFullWidthBottom = action === 'edit' || action === 'reply';

  // Determine the container className based on the action
  const containerClassName = isFullWidthBottom 
    ? "max-w-3xl md:ml-64 fixed bottom-0 left-0 right-0 z-50 bg-eoe-bg dark:bg-eoe-dark-bg p-3 border-t border-eoe-border dark:border-eoe-dark-border shadow-md" 
    : "mb-4 mt-2 flex flex-col gap-2";

  const getActionTitle = () => {
    if (action === 'reply' && commentData) {
      //console.log(commentData.content)
      return (
      <>
        <p className='text-xs'>Replying to <a href={`/user/${commentData.username}`} className="text-eoe-text dark:text-eoe-dark-text internal-link px-1">@{commentData.username}</a> | {commentData.updated_at ? (formatRelativeTime(commentData.updated_at)) + ' ago (' + formatDate(commentData.updated_at) + ')' : formatRelativeTime(commentData?.created_at || '')}</p>
        <div className="p-1 rounded-md bg-eoe-icon dark:bg-eoe-dark-icon border-l-4 border-gray-300 dark:border-eoe-dark-text-heading text-base italic leading-relaxed text-eoe-text-heading dark:text-eoe-dark-text-heading">
          <blockquote className="italic font-bold leading-relaxed text-eoe-text-heading dark:text-eoe-dark-text-heading line-clamp-2">
            {transformHtmlContent(commentData?.content || '')}
          </blockquote>
        </div>
      </>
      );
    } else if (action === 'edit') {
      return `Editing your comment ${commentData?.updated_at ? (formatRelativeTime(commentData?.updated_at)) + ' ago (' + formatDate(commentData?.updated_at) + ')' : formatRelativeTime(commentData?.created_at || '')}`;
    }
    return null;
  };

  useEffect(() => {
    //console.log('commentData', commentData?.id, commentData?.user_id, commentData?.properties?.coauthors)
    if (commentData?.id && currentUser?.id === commentData?.user_id) {
      loadCoauthorData(
        {
          id: commentData?.id,
          user_id: commentData.user_id || 0,
          properties: {
            coauthors: commentData?.properties?.coauthors || []
          }
        },
        'comment',
        currentUser?.id,
        {
          setExistingCoauthors,
          setPendingCoauthors,
          setIsLoadingCoauthors
        }
      );
    }
  }, [commentData?.id, commentData?.user_id, commentData?.properties?.coauthors, currentUser?.id]);

  const handleCoauthorSelectEdit = (suggestion: CoauthorSuggestion) => {
    selectCoauthor(
      suggestion,
      existingCoauthors,
      newCoauthors,
      setNewCoauthors,
      onCoauthorsChange,
      removedCoauthors
    );
  };

  const handleCoauthorRemoveEdit = async (coauthor: CoauthorSuggestion) => {
    await removeCoauthor(
      coauthor,
      {
        setNewCoauthors,
        setRemovedCoauthors,
        setPendingCoauthors,
        setExistingCoauthors
      },
      {
        newCoauthors,
        removedCoauthors
      },
      onCoauthorsChange
    );
  };

  const handleCoauthorSelect = (suggestion: CoauthorSuggestion) => {
    if (!coauthors.some(c => c.id === suggestion.id && c.type === suggestion.type)) {
        setCoauthors([...coauthors, suggestion]);
    }
  };

  const handleCoauthorRemove = (coauthor: CoauthorSuggestion) => {
      setCoauthors(coauthors.filter(c => !(c.id === coauthor.id && c.type === coauthor.type)));
  };

  const actionTitle = getActionTitle();

  return (
    <div className={containerClassName} ref={inputRef}>
      {actionTitle && (
        <div className="text-sm font-medium mb-2 text-eoe-text dark:text-eoe-dark-text">
          {actionTitle}
        </div>
      )}
      <TextEditor
        key={editorKey}
        onChange={setContent}
        value={content}
        editorType="comment"
        action={action}
        isEditorExpanded={isExpanded}
      >
        {action === 'add' && (
          <div className="flex flex-row justify-between items-center w-full px-3">
            <div className="flex-shrink-0">
              <EntityPrivacy
                privacy={privacy}
                onPrivacyChange={(newPrivacy) => setPrivacy(newPrivacy)}
                actorType="user"
                editMode={false}
                miniIcon={true}
              />
            </div>
            <div className="flex items-center gap-2">
              {(action === 'add') && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-secondary flex-shrink-0 rounded-full flex items-center gap-2 text-xs font-semibold hover:text-white cursor-pointer"
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!content.trim() || content.trim() === '<p></p>'}
                className={`flex-shrink-0 ${content.trim() && content.trim() !== '<p></p>' ? 'btn-primary cursor-pointer' : 'btn-primary-disabled hover:btn-primary hover:cursor-not-allowed'} rounded-full flex items-center gap-1 text-xs font-semibold hover:text-white`}
              >
                <Send className="size-3"/>
                {buttonText}
              </button>
            </div>
          </div>
        )}
        
        {action !== 'add' && (
          <div className="flex flex-row justify-between flex-wrap w-full gap-2 px-3">
            <div className="flex-shrink-0">
              <EntityPrivacy
                privacy={privacy}
                onPrivacyChange={(newPrivacy) => setPrivacy(newPrivacy)}
                actorType="user"
                editMode={false}
                miniIcon={true}
              />
            </div>
            <div className="flex gap-2 items-center">
              {(action === 'reply' || action === 'edit') && (
                <button
                  type="button"
                  onClick={handleCancel}
                  className="btn-secondary flex-shrink-0 rounded-full flex items-center gap-2 text-xs font-semibold hover:text-white cursor-pointer"
                >
                  Cancel
                </button>
              )}
              <button
                type="button"
                onClick={handleSubmit}
                disabled={!content.trim() || content.trim() === '<p></p>'}
                className={`flex-shrink-0 ${content.trim() && content.trim() !== '<p></p>' ? 'btn-primary cursor-pointer' : 'btn-primary-disabled hover:btn-primary hover:cursor-not-allowed'} rounded-full flex items-center gap-1 text-xs font-semibold hover:text-white`}
              >
                <Send className="size-3"/>
                {buttonText}
              </button>
            </div>
          </div>
        )}
      </TextEditor>
      {content.trim() && content.trim() !== '<p></p>' && action !== 'edit' && (
        <div className={`${action === 'add' ? '' : 'my-2'}`}>
          <div className="flex flex-col gap-1">
              <CoauthorSelect
                inputType="add"
                placeholder="Add collaborators"
                className="w-full"
                onSelect={handleCoauthorSelect}
                excludeIds={coauthors.map(c => c.id)}
              />
              {coauthors.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {coauthors.map((coauthor) => (
                    <EntityAvatar
                      key={`${coauthor.type}-${coauthor.id}`}
                      entity={coauthor as any}
                      entityType={coauthor.type}
                      entityChip={true}
                      showLink={false}
                      showCloseChip={true}
                      onEntityChipClose={() => handleCoauthorRemove(coauthor)}
                    />
                  ))}
                </div>
              )}
          </div>
        </div>
      )}
      {action === 'edit' && (
        <DetailPanel 
          title={`Collaborators (${existingCoauthors.length + pendingCoauthors.length})`} 
          className="mt-1 mb-2 text-sm bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal"
          defaultOpen={true}
        >
          {isLoadingCoauthors ? (
            <div className="p-2 text-center">Loading collaborators...</div>
          ) : (
            <div className="space-y-2">
              <div className="flex flex-col gap-2">
                <CoauthorSelect
                  inputType="add"
                  placeholder="Add collaborators"
                  className="w-full"
                  onSelect={handleCoauthorSelectEdit}
                />
              </div>
              {existingCoauthors.map((coauthor) => (
                <EntityAvatar
                  key={`existing-${coauthor.type}-${coauthor.id}`}
                  entity={coauthor as any}
                  entityType={coauthor.type}
                  entityChip={true}
                  showCloseChip={true}
                  isInputField={false}
                  showLink={false}
                  onEntityChipClose={() => handleCoauthorRemoveEdit(coauthor)}
                />
              ))}
              {pendingCoauthors.map((coauthor) => (
                <EntityAvatar
                  key={`pending-${coauthor.type}-${coauthor.id}`}
                  entity={coauthor as any}
                  entityType={coauthor.type}
                  entityChip={true}
                  showCloseChip={true}
                  isInputField={false}
                  showLink={false}
                  badgeElement={<Clock className="size-3.5 text-yellow-500 dark:text-yellow-300" />}
                  onEntityChipClose={() => handleCoauthorRemoveEdit(coauthor)}
                />
              ))}
              {newCoauthors.map((coauthor) => (
                <EntityAvatar
                  key={`new-${coauthor.type}-${coauthor.id}`}
                  entity={coauthor as any}
                  entityType={coauthor.type}
                  entityChip={true}
                  showCloseChip={true}
                  isInputField={false}
                  showLink={false}
                  badgeElement={<Clock className="size-3.5 text-yellow-500 dark:text-yellow-300" />}
                  onEntityChipClose={() => handleCoauthorRemoveEdit(coauthor)}
                />
              ))}
            </div>
          )}
        </DetailPanel>
      )}
      {error && (
        <p className="mt-1 text-sm text-red-500">{error}</p>
      )}
    </div>
  );
});

export default CommentInput;